/**
 * <s-collection-filters> — Key Leaves sidebar filtering, ported from the live theme.
 *
 * Products render once (whole collection on one page) and are shown or hidden in place.
 * Options in a group are OR'd; groups are AND'd. A checked sub-option (Saxophone > Alto)
 * replaces its parent within the group so it narrows results.
 * State mirrors to the URL as ?<group>=<value>,<value>. Legacy ?filter=clean and #clean links work.
 *
 * Data contract (written by templates/collection.liquid and blocks/collection-filter-option.liquid):
 *   host:    data-collection, data-clear-url, data-new-days, data-count-text, data-apply-text
 *   item:    [data-filter-item] data-collections data-tags data-on-sale data-available data-created
 *   option:  [data-filter-option] data-group data-value data-match data-collection data-tag data-parent
 *   regions: [data-filter-count] [data-filter-apply] [data-filter-active] [data-filter-chips]
 *            [data-filter-empty] [data-filter-clear] select[data-filter-sort]
 *
 * Commands (on this element): --open, --close, --expand (source inside an option), --clear
 */
class SCollectionFilters extends HTMLElement {
  #ac;
  #items = [];
  #options = [];
  #expanded = new Set();
  #locked = null;
  #collectionChip = false;
  #cutoff = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;

    this.#cutoff = Date.now() / 1000 - (Number(this.dataset.newDays) || 60) * 86400;

    this.#items = [...this.querySelectorAll('[data-filter-item]')].map((el) => ({
      el,
      collections: new Set((el.dataset.collections || '').split(' ').filter(Boolean)),
      tags: new Set((el.dataset.tags || '').split(' ').filter(Boolean)),
      onSale: el.dataset.onSale === 'true',
      available: el.dataset.available === 'true',
      created: Number(el.dataset.created) || 0,
    }));

    this.#options = [...this.querySelectorAll('[data-filter-option]')].map((el) => ({
      el,
      input: el.querySelector('input[type="checkbox"]'),
      expand: el.querySelector('[command="--expand"]'),
      key: `${el.dataset.group}:${el.dataset.value}`,
      group: el.dataset.group,
      value: el.dataset.value,
      label: (el.querySelector('[data-filter-label]')?.textContent || el.dataset.value).trim(),
      match: el.dataset.match,
      collection: el.dataset.collection || '',
      tag: el.dataset.tag || '',
      parent: el.dataset.parent ? `${el.dataset.group}:${el.dataset.parent}` : null,
    }));

    this.#readState();

    this.addEventListener('change', (event) => this.#onChange(event), { signal });
    this.addEventListener('command', (event) => this.#onCommand(event), { signal });
    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.querySelector('[data-filter-sort]')?.addEventListener('change', (event) => this.#onSort(event), { signal });
    document.addEventListener(
      'keyup',
      (event) => {
        if (event.key === 'Escape' && this.hasAttribute('data-drawer-open')) this.#toggleDrawer(false);
      },
      { signal }
    );

    this.#apply(false);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    document.documentElement.classList.remove('overflow-hidden');
  }

  /* State */

  #readState() {
    const params = new URLSearchParams(window.location.search);

    for (const option of this.#options) {
      const values = (params.get(option.group) || '').split(',');
      if (values.includes(option.value)) option.input.checked = true;
    }

    const legacy = (params.get('filter') || window.location.hash.replace('#', '')).toLowerCase();
    if (legacy) {
      const option = this.#options.find((o) => o.value === legacy);
      if (option) option.input.checked = true;
    }

    // On an option's own collection page (e.g. /collections/saxophone) that option starts checked.
    const current = this.dataset.collection;
    if (current && current !== 'all') {
      this.#locked = this.#options.find((o) => o.collection === current) || null;
      if (this.#locked) this.#locked.input.checked = true;
      this.#collectionChip = !this.#locked && Boolean(this.dataset.collectionTitle);
    }

    for (const option of this.#options) {
      if (!option.input.checked) continue;
      if (option.parent) this.#expanded.add(option.parent);
      if (option.expand) this.#expanded.add(option.key);
    }
  }

  #writeUrl() {
    const params = new URLSearchParams(window.location.search);
    const groups = new Set(this.#options.map((o) => o.group));
    for (const key of ['filter', 'mode', ...groups]) params.delete(key);

    for (const [group, values] of this.#checkedValuesByGroup()) params.set(group, values.join(','));

    const query = params.toString().replace(/%2C/g, ',');
    history.replaceState(history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }

  #checkedValuesByGroup() {
    const byGroup = new Map();
    for (const option of this.#options) {
      if (!option.input.checked || option === this.#locked) continue;
      if (!byGroup.has(option.group)) byGroup.set(option.group, []);
      byGroup.get(option.group).push(option.value);
    }
    return byGroup;
  }

  /* Matching */

  #matches(option, item) {
    if (option.match === 'on_sale' && item.onSale) return true;
    if (option.match === 'new_arrival' && item.created >= this.#cutoff) return true;
    if (option.match === 'in_stock' && item.available) return true;
    if (option.collection && item.collections.has(option.collection)) return true;
    if (option.tag && item.tags.has(option.tag)) return true;
    return false;
  }

  /** Group -> effective checked options; a checked parent with a checked child is dropped. */
  #activeGroups(excludeGroup) {
    const checked = this.#options.filter((o) => o.input.checked);
    const parentsWithChild = new Set(checked.filter((o) => o.parent).map((o) => o.parent));
    const groups = new Map();
    for (const option of checked) {
      if (option.group === excludeGroup || parentsWithChild.has(option.key)) continue;
      if (!groups.has(option.group)) groups.set(option.group, []);
      groups.get(option.group).push(option);
    }
    return groups;
  }

  #passes(item, groups) {
    for (const options of groups.values()) {
      if (!options.some((option) => this.#matches(option, item))) return false;
    }
    return true;
  }

  /* Render */

  #apply(updateUrl = true) {
    const groups = this.#activeGroups();
    let shown = 0;
    for (const item of this.#items) {
      const visible = this.#passes(item, groups);
      item.el.hidden = !visible;
      if (visible) shown += 1;
    }

    this.#renderCount(shown);
    this.#renderOptions();
    this.#renderChips();

    const empty = this.querySelector('[data-filter-empty]');
    if (empty) empty.hidden = shown > 0 || this.#items.length === 0;
    if (updateUrl) this.#writeUrl();
  }

  #renderCount(shown) {
    const total = this.#items.length;
    const fill = (text) => (text || '').replace('[shown]', shown).replace('[total]', total);
    const count = this.querySelector('[data-filter-count]');
    if (count) count.textContent = fill(this.dataset.countText);
    const apply = this.querySelector('[data-filter-apply]');
    if (apply) apply.textContent = fill(this.dataset.applyText);
  }

  #renderOptions() {
    const cache = new Map();
    for (const option of this.#options) {
      if (!cache.has(option.group)) cache.set(option.group, this.#activeGroups(option.group));
      const others = cache.get(option.group);
      const count = this.#items.filter((item) => this.#matches(option, item) && this.#passes(item, others)).length;

      const empty = count === 0 && !option.input.checked;
      const collapsed = option.parent && !this.#expanded.has(option.parent);
      option.el.hidden = Boolean(empty || collapsed);

      if (option.expand) {
        const open = this.#expanded.has(option.key);
        option.expand.setAttribute('aria-expanded', String(open));
        option.el.toggleAttribute('data-expanded', open);
      }
    }
  }

  #renderChips() {
    const row = this.querySelector('[data-filter-active]');
    const chips = this.querySelector('[data-filter-chips]');
    const template = this.querySelector('template[data-filter-chip]');
    if (!row || !chips || !template) return;

    const checked = this.#options.filter((o) => o.input.checked);
    row.hidden = checked.length === 0 && !this.#collectionChip;

    const build = (label, key) => {
      const chip = template.content.firstElementChild.cloneNode(true);
      chip.dataset.filterRemove = key;
      chip.querySelector('[data-filter-chip-label]').textContent = label;
      chip.setAttribute('aria-label', (template.dataset.removeLabel || '%s').replace('%s', label));
      return chip;
    };

    const nodes = [];
    if (this.#collectionChip) nodes.push(build(this.dataset.collectionTitle, 'collection'));
    for (const option of checked) nodes.push(build(option.label, option.key));
    chips.replaceChildren(...nodes);
  }

  /* Events */

  #onChange(event) {
    const option = this.#options.find((o) => o.input === event.target);
    if (!option) return;
    if (option === this.#locked && !option.input.checked) return this.#leaveCollection();
    if (option.expand && option.input.checked) this.#expanded.add(option.key);
    this.#apply();
  }

  #onClick(event) {
    const chip = event.target.closest('[data-filter-remove]');
    if (!chip) return;
    const key = chip.dataset.filterRemove;
    if (key === 'collection') return this.#leaveCollection();
    const option = this.#options.find((o) => o.key === key);
    if (!option) return;
    option.input.checked = false;
    if (option === this.#locked) return this.#leaveCollection();
    this.#apply();
  }

  #onCommand(event) {
    switch (event.command) {
      case '--open':
        return this.#toggleDrawer(true, event.source);
      case '--close':
        return this.#toggleDrawer(false);
      case '--expand': {
        const el = event.source?.closest('[data-filter-option]');
        const option = this.#options.find((o) => o.el === el);
        if (!option) return;
        this.#expanded.has(option.key) ? this.#expanded.delete(option.key) : this.#expanded.add(option.key);
        return this.#renderOptions();
      }
      case '--clear':
        // On a specific collection page, clearing goes back to All Products.
        if (this.#locked || this.#collectionChip) return (window.location.href = this.dataset.clearUrl);
        for (const option of this.#options) option.input.checked = false;
        return this.#apply();
    }
  }

  #onSort(event) {
    const params = new URLSearchParams(window.location.search);
    params.set('sort_by', event.target.value);
    params.delete('page');
    window.location.search = params.toString().replace(/%2C/g, ',');
  }

  #leaveCollection() {
    const params = new URLSearchParams();
    for (const [group, values] of this.#checkedValuesByGroup()) params.set(group, values.join(','));
    const query = params.toString().replace(/%2C/g, ',');
    window.location.href = `${this.dataset.clearUrl}${query ? `?${query}` : ''}`;
  }

  #toggleDrawer(open, source) {
    this.toggleAttribute('data-drawer-open', open);
    document.documentElement.classList.toggle('overflow-hidden', open);
    const opener = this.querySelector('[command="--open"]');
    opener?.setAttribute('aria-expanded', String(open));
    if (open) {
      this.querySelector('[data-filter-panel] input, [data-filter-panel] summary')?.focus({ preventScroll: true });
    } else if (opener && window.matchMedia('(max-width: 1023px)').matches) {
      opener.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('s-collection-filters')) customElements.define('s-collection-filters', SCollectionFilters);
